import { getMomentTz, TimeService } from "@dexteel/mesf-core";
import { CalendarToday, List as ListIcon } from "@mui/icons-material";
import { Box, Button, ButtonGroup, Paper, Typography } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { CustomEventsTable } from "./components/CustomEventsTable";
import { EnhancedMonthCalendar } from "./components/EnhancedMonthCalendar";
import { useToday } from "../hooks/useToday";
import { useSearchEvents } from "./hooks/useSearchEvents";
import { addDays, startOfMonth, startOfWeek } from "./utils/calendarUtils";

const moment = getMomentTz();

const calculateCalendarDateBounds = (referenceDate: Date) => {
	const monthStart = startOfMonth(referenceDate);
	const calendarStart = startOfWeek(monthStart, 0);

	const calendarDays: Date[] = [];
	let day = calendarStart;
	for (let i = 0; i < 42; i++) {
		calendarDays.push(new Date(day));
		day = addDays(day, 1);
	}

	const serverTz = TimeService.getInstance().getServerTimeZone();

	const cStart = moment.tz(calendarDays[0], serverTz).toDate();
	cStart.setHours(0, 0, 0, 0);

	const cEnd = moment
		.tz(calendarDays[calendarDays.length - 1], serverTz)
		.toDate();
	cEnd.setHours(23, 59, 59, 999);

	return { start: cStart, end: cEnd };
};

const isSameMonthAsToday = (date: Date) => {
	const now = new Date();
	return (
		date.getMonth() === now.getMonth() &&
		date.getFullYear() === now.getFullYear()
	);
};

type Props = {
	onError: (error: Error) => void;
};

export const MillCalendarCard = ({ onError }: Props) => {
	const [displayMonth, setDisplayMonth] = useState<Date>(() => {
		const serverTz = TimeService.getInstance().getServerTimeZone();
		return moment.tz(serverTz).date(1).startOf("day").toDate();
	});
	const [{ start, end }, setDateBounds] = useState(() =>
		calculateCalendarDateBounds(displayMonth),
	);
	const [view, setView] = useState<"calendar" | "list">("calendar");

	const isViewingToday = isSameMonthAsToday(displayMonth);

	const calendarTitle = useMemo(
		() =>
			displayMonth.toLocaleDateString("en-US", {
				month: "long",
				year: "numeric",
			}),
		[displayMonth],
	);

	const { data } = useSearchEvents({ start, end, onError });

	const goToMonth = (newMonth: Date) => {
		setDisplayMonth(newMonth);
		setDateBounds(calculateCalendarDateBounds(newMonth));
	};

	// Auto-advance at month rollover (server TZ) if the user was viewing the
	// current month — derived from the previous day key, because at flip time
	// isViewingToday already points at the new month.
	const todayKey = useToday();
	const prevDayRef = useRef(todayKey);
	useEffect(() => {
		const prevKey = prevDayRef.current;
		if (prevKey === todayKey) return;
		prevDayRef.current = todayKey;
		const serverTz = TimeService.getInstance().getServerTimeZone();
		const prev = moment.tz(prevKey, serverTz);
		const next = moment.tz(todayKey, serverTz);
		const wasViewingCurrentMonth =
			displayMonth.getMonth() === prev.month() &&
			displayMonth.getFullYear() === prev.year();
		const monthChanged =
			next.month() !== prev.month() || next.year() !== prev.year();
		if (wasViewingCurrentMonth && monthChanged) {
			goToMonth(next.clone().date(1).startOf("day").toDate());
		}
	});

	const handlePrevious = () => {
		goToMonth(
			new Date(displayMonth.getFullYear(), displayMonth.getMonth() - 1, 1),
		);
	};

	const handleNext = () => {
		goToMonth(
			new Date(displayMonth.getFullYear(), displayMonth.getMonth() + 1, 1),
		);
	};

	const handleToday = () => {
		const serverTz = TimeService.getInstance().getServerTimeZone();
		goToMonth(moment.tz(serverTz).date(1).startOf("day").toDate());
	};

	return (
		<Paper
			elevation={1}
			sx={{
				p: 1,
				height: "100%",
				display: "grid",
				gridTemplateRows: "auto minmax(0, 1fr)",
			}}
		>
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					flexWrap: "wrap",
					gap: 1,
					pb: 1,
				}}
			>
				<Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
					<ButtonGroup size="small">
						<Button variant="outlined" onClick={handlePrevious}>
							Previous
						</Button>
						<Button
							variant={isViewingToday ? "contained" : "outlined"}
							onClick={handleToday}
							color="primary"
						>
							Today
						</Button>
						<Button variant="outlined" onClick={handleNext}>
							Next
						</Button>
					</ButtonGroup>
					<ButtonGroup size="small">
						<Button
							variant={view === "calendar" ? "contained" : "outlined"}
							startIcon={<CalendarToday />}
							onClick={() => setView("calendar")}
							color="primary"
						>
							Calendar
						</Button>
						<Button
							variant={view === "list" ? "contained" : "outlined"}
							startIcon={<ListIcon />}
							onClick={() => setView("list")}
							color="primary"
						>
							List
						</Button>
					</ButtonGroup>
				</Box>
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
					}}
				>
					<Typography
						variant="caption"
						sx={{
							color: "#666",
							fontWeight: 500,
							lineHeight: 1,
							letterSpacing: 0.5,
						}}
					>
						Calendar M2
					</Typography>
					<Typography sx={{ fontSize: "1.25rem", fontWeight: 600 }}>
						{calendarTitle}
					</Typography>
				</Box>
			</Box>

			<Box sx={{ minHeight: 0, overflow: "auto" }}>
				{view === "calendar" ? (
					<EnhancedMonthCalendar
						events={data || []}
						initialDate={displayMonth}
					/>
				) : (
					<CustomEventsTable
						month={displayMonth.getMonth()}
						year={displayMonth.getFullYear()}
						onError={onError}
					/>
				)}
			</Box>
		</Paper>
	);
};
